import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  Input,
  OnChanges,
  OnDestroy
} from "@angular/core";
import { BarGraphData } from "../charts/barGraph/bar-graph.component";
import { DonutGraphData } from "../charts/donutGraph/donut-graph.component";
import { CdrSummaryStatisticUserService } from "@onsip/common/services/api/resources/cdrSummaryStatistic/cdr-summary-statistic-user.service";
import { SnackbarService } from "@onsip/web/features/shared/components/snackbar/snackbar.service";
import { UserService } from "@onsip/common/services/api/resources/user/user.service";
import { BehaviorSubject, debounceTime, fromEvent, startWith, Subscription, take } from "rxjs";

interface CachedResult {
  barGraphData: Array<BarGraphData>;
  donutGraphData: Array<DonutGraphData>;
}

@Component({
  selector: "onsip-pbx-outbound-calling-activity",
  templateUrl: "./outbound-calling-activity.component.html",
  styleUrls: ["./outbound-calling-activity.component.scss"],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class OutboundCallingActivityComponent implements OnChanges, OnDestroy, AfterViewInit {
  @Input() dateStart!: string;
  @Input() dateEnd!: string;

  cachedResult: Record<string, CachedResult> = {};
  donutGraphData: Array<DonutGraphData> = [];
  barGraphData: Array<BarGraphData> = [];
  barGraphWidth = 0;
  loadingStateComplete$ = new BehaviorSubject(true);

  private unsubscriber = new Subscription();

  constructor(
    private cdrSummaryStatistic: CdrSummaryStatisticUserService,
    private userService: UserService,
    private snackbar: SnackbarService,
    private cdRef: ChangeDetectorRef,
    private hostElement: ElementRef
  ) {}

  ngAfterViewInit(): void {
    this.handleWindowResize();
  }

  ngOnChanges(): void {
    this.handleInputChange();
  }

  ngOnDestroy(): void {
    this.unsubscriber.unsubscribe();
  }

  private handleInputChange(): void {
    const dateRange = this.dateStart + "-" + this.dateEnd;
    if (this.cachedResult[dateRange]) {
      this.barGraphData = this.cachedResult[dateRange].barGraphData;
      this.donutGraphData = this.cachedResult[dateRange].donutGraphData;
      return;
    } else {
      this.loadingStateComplete$.next(false);
      this.cdrSummaryStatistic
        .cdrSummaryStatisticUser({ StartDateTime: this.dateStart, EndDateTime: this.dateEnd })
        .then(result => {
          if (result.status === "success") {
            const data = Object.values(result.data);
            // convert data to bar graph format and then also filter out zero calls entries
            const parseData: Array<BarGraphData> = data
              .map(v => ({
                name: v.name,
                value: Number(v.termination)
              }))
              .filter(v => v.value > 0)
              .sort((a, b) => b.value - a.value);
            this.cachedResult[dateRange] = { barGraphData: [], donutGraphData: [] } as CachedResult;
            this.cachedResult[dateRange].barGraphData = parseData;
            this.barGraphData = parseData;
            this.cdRef.markForCheck();
            this.getDonutInfo(parseData.length, dateRange);
          } else {
            this.snackbar.openSnackBar(result.data.message, "error");
            this.cdrSummaryStatistic.clearErrors();
          }
          this.loadingStateComplete$.next(true);
        });
    }
  }

  private getDonutInfo(activeUsers: number, dateRange: string): void {
    this.unsubscriber.add(
      this.userService.orgState.pipe(take(1)).subscribe(users => {
        const inactiveUsers = users.length - activeUsers;
        const donutData = [
          {
            name: "inactive",
            value: inactiveUsers
          },
          {
            name: "active",
            value: activeUsers
          }
        ];
        this.cachedResult[dateRange].donutGraphData = donutData;
        this.donutGraphData = donutData;
        this.cdRef.markForCheck();
      })
    );
  }

  private handleWindowResize(): void {
    // adjust width of the bar graph depending on the current window size if that changes
    this.unsubscriber.add(
      fromEvent(window, "resize")
        .pipe(startWith(true), debounceTime(500))
        .subscribe(() => {
          this.adjustBarGraphWidth();
          this.cdRef.markForCheck();
        })
    );
  }

  adjustBarGraphWidth(): void {
    this.barGraphWidth = this.hostElement.nativeElement.offsetWidth;
    if (window.innerWidth >= 600) {
      // remove width space for the donut graph
      this.barGraphWidth -= 155;
    }
  }
}
